import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ChevronDown, ChevronUp, Link2, Pencil } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Platform } from '../../../types/Platforms';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { SlideOverShell, SectionTitle, OverviewCard, BreakdownCard } from '../../ui/slideOverParts';
import { currencyFormatter } from '../../../services/helperService';
import { formatNormalizedUnitPrice, StdUnit } from '../../../services/inventory/inventoryService';
import { getCategoryStyles } from '../../orderhistory/categoryUtils';
import { useAppSettingsStore } from '../../../stores/appSettingsStore';
import {
  iconBgAmber,
  greenTextColor,
  redTextColor,
  mutedText,
  tertiaryText,
} from '../../../styling/stylingHelper';
import { badgeBaseClass, grayBadgeClass, STATUS_TINT, CONFIDENCE_TINT } from './inventoryReviewShared';
import { VariancePoolMember, VarianceRow } from '../../../utils/reports/inventoryVariance';
import { prepPathLines } from '../../../utils/reports/prepPathLabel';
import { getTableFormattedDate } from '../../../utils/dateUtils';
import { UsageWithSetup } from './leakageProductSetups';
import { hasComparableQty, LeakageQtyFormat, LEAKAGE_QTY_STD } from './leakagePoolDisplay';
import { leakageSetupDish } from '../../onboarding/inventoryValues';

/** A reconciled row, optionally carrying its group name (all-groups combined view). */
export type LeakageDrawerRow = VarianceRow & { groupName?: string | null };

/**
 * A usage entry enriched with the cost-price analysis behind it. `buildPerDishUsage`
 * carries no id — the caller matches the dish label against the cost-price store — so
 * the field is optional and only entries that resolved get an Edit button.
 */
export type LeakageDishUsage = UsageWithSetup;

/** Identity of the order-history product behind an inventory row. */
export interface LinkedOrderProduct {
  productName: string;
  platform: string;
  unknownSupplierName?: string;
  articleId?: string;
  /**
   * €/std-unit the leakage math valued this product at (the row's price chain), NOT the raw
   * invoice line price. Showing an invoice figure next to numbers computed on adjusted prices
   * is exactly the mismatch dataRules warns about — per-kg platforms rescale, and facturen
   * override order prices at read time.
   */
  pricePerStdUnit: number | null;
  /** Std unit `pricePerStdUnit` is per (normalised for display: g→kg, ml→L). */
  stdUnit: string;
  lastOrderDate: Date | null;
}

interface LeakageProductDrawerProps {
  /** The row to explain. `null` closes the drawer. */
  row: LeakageDrawerRow | null;
  /** Per-dish sales usage for this row's key, already scoped to the report window. */
  usage: LeakageDishUsage[] | null;
  /** Report period, shown above the product name. */
  periodLabel?: string;
  /** The tab's active quantity formatter (std units or the unit staff count in). */
  qtyFormat: LeakageQtyFormat;
  /** Supplier behind this product, when the caller could resolve one. */
  supplier?: { platform: string; name?: string } | null;
  /** Live (order-derived) product category, preferred over the frozen inventory one. */
  liveCategory?: string | null;
  /** The order-history product this inventory row resolves to (last order carrying it). */
  linkedProduct?: LinkedOrderProduct | null;
  onClose: () => void;
  /** Open the cost-price editor for one of the usage entries. */
  onEditDish?: (costPriceId: string) => void;
  /** Start the link flow for this product (no costed dish uses it yet). */
  onAddLink?: () => void;
  /** Open the inventory link editor for this product's section assignment. */
  onEditInventoryLink?: () => void;
  /** One of the modals this drawer's actions open is stacked on top — see `SlideOverShell`. */
  overlayOpen?: boolean;
}

const CAUSE_KEYS = [
  'causeWaste',
  'causeStaff',
  'causePours',
  'causeTransfers',
  'causeRecount',
  'causeSalesMapping',
  'causeInvoices',
] as const;

/** Money, or `undefined` so the card simply drops its sub-line when there is no price. */
const money = (v: number | null): string | undefined => (v === null ? undefined : currencyFormatter.format(v));

/** Red on loss, green on gain, muted when flat — the table's convention for a delta. */
const deltaTone = (v: number): string => (v < 0 ? redTextColor : v > 0 ? greenTextColor : mutedText);

const Tint: React.FC<{ tint: { bg: string; color: string } | null; label: string }> = ({ tint, label }) =>
  tint ? (
    <span className={badgeBaseClass} style={{ backgroundColor: tint.bg, color: tint.color }}>
      {label}
    </span>
  ) : (
    <span className={grayBadgeClass}>{label}</span>
  );

/**
 * Right-side drawer explaining one leakage row: the start → purchases → usage → expected
 * → counted reconciliation as cards, where the count moved per section, which inventory
 * link the row hangs off, and HOW the theoretical usage was derived (per-dish
 * `per dish × sold = usage` formula cards).
 *
 * Purely presentational — every number arrives on `row` / `usage` already computed by
 * `inventoryVariance.ts`; nothing here recomputes leakage.
 */
export const LeakageProductDrawer: React.FC<LeakageProductDrawerProps> = ({
  row,
  usage,
  periodLabel,
  qtyFormat,
  supplier,
  liveCategory,
  linkedProduct,
  onClose,
  onEditDish,
  onAddLink,
  onEditInventoryLink,
  overlayOpen,
}) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const [causesOpen, setCausesOpen] = useState(false);

  // Both memos read the changes straight off `row` rather than a `?? []` local: as a
  // dependency that local would be a fresh array on every render, recomputing both.
  const sectionTotals = useMemo(
    () =>
      (row?.sectionChanges ?? []).reduce(
        (acc, s) => ({
          qty: acc.qty + s.deltaQty,
          value: s.deltaValue === null ? acc.value : (acc.value ?? 0) + s.deltaValue,
        }),
        { qty: 0, value: null as number | null }
      ),
    [row]
  );

  const sectionNames = useMemo(() => {
    const names = (row?.sectionChanges ?? []).map((s) => s.sectionName).filter(Boolean);
    if (names.length === 0 && row?.attributedSectionName) return [row.attributedSectionName];
    return Array.from(new Set(names));
  }, [row]);

  const flags = useMemo(() => {
    if (!row) return [];
    const out: { key: string; label: string }[] = [];
    if (row.moved) out.push({ key: 'moved', label: t('inventory.leakage.flag.moved') });
    if (row.alsoInGroups.length > 0)
      out.push({ key: 'also', label: t('inventory.leakage.flag.alsoIn', { groups: row.alsoInGroups.join(', ') }) });
    if (row.hasUnmeasurablePurchases) out.push({ key: 'unm', label: t('inventory.leakage.flag.unmeasurable') });
    if (row.countUnitMismatch) out.push({ key: 'cu', label: t('inventory.leakage.flag.countUnit') });
    if (row.neededUnitMismatch) out.push({ key: 'ru', label: t('inventory.leakage.flag.recipeUnit') });
    if (row.noRecipeCoverage) out.push({ key: 'nr', label: t('inventory.leakage.flag.noRecipe') });
    return out;
  }, [row, t]);

  if (!row) return null;

  const sectionChanges = row.sectionChanges;
  const usageRows = usage ?? [];
  const hasUsage = usageRows.length > 0;
  const totalSold = usageRows.reduce((s, u) => s + u.unitsSold, 0);

  // Alternative pool: this row reconciles several interchangeable products at once. When their
  // std units disagree there is no single quantity in any space, and the engine sends null for
  // every quantity field. Both signals are checked — the null and the flag — so the cell stays
  // a dash whichever one arrives.
  const pool = row.pool;
  const members = row.members ?? [];
  const qtyComparable = hasComparableQty(row);
  // Every quantity in this drawer goes through the shared, tested guards — including the
  // SECTION rows, whose quantities the engine leaves at literal 0 on a mixed-unit pool
  // (it only accumulates them `if (comparable)`), so an unguarded cell prints "0 → 0" as
  // if the section had been counted empty. Only the € figures are real on those rows.
  const poolQty = (qty: number | null): string => qtyFormat.row(row, qty);
  const memberQty = (m: VariancePoolMember, qty: number | null): string => qtyFormat.member(m, qty);
  // The "Needed quantity breakdown" below stays in STD units whatever the tab's toggle says:
  // that block is recipe territory and reads in the unit the recipe is written in, so keeping
  // it whole avoids "120 g per dish x 30 sold = 4 fles" inside one card.
  const stdQty = (qty: number | null): string => LEAKAGE_QTY_STD.row(row, qty);
  // The recipes hold this product in a unit space nothing could convert into the count's,
  // so the engine dropped its usage. Every per-dish figure below is then a recipe-space
  // number the row's unit would mislabel — the exact "3000 pcs of wine" failure.
  const usageUnreadable = !!row.neededUnitMismatch && !row.neededQty;

  // Same normalisation the relink picker uses, so a €/g price reads as €/kg here too.
  const valuationPrice = linkedProduct
    ? formatNormalizedUnitPrice({
        // A mixed-unit pool carries '' here, which matches no branch → no price shown, which
        // is right: there is no single €/unit for products measured in different spaces.
        stdUnit: linkedProduct.stdUnit as StdUnit,
        pricePerStdUnit: linkedProduct.pricePerStdUnit,
        // Only read for the 'unit' std unit, where €/std-unit IS the per-unit price.
        latestUnitPrice: linkedProduct.pricePerStdUnit ?? 0,
      })
    : null;

  // The overview's leakage tile follows the row's own verdict rather than the sign, so a
  // small negative on an OK row stays neutral instead of shouting red.
  const leakageTone: 'bad' | 'warn' | 'neutral' =
    row.status === 'highRisk' || row.status === 'review' ? 'bad' : row.status === 'overCounted' ? 'warn' : 'neutral';

  // Category label: `liveCategory` and `inventoryCategoryName` are already display names,
  // not the raw category enums `formatCategoryName` keys off — so the name renders as-is
  // (the emoji/colour map would fall through to its default for every one of them).
  const categoryLabel = liveCategory ?? row.inventoryCategoryName;

  const addLinkButton = onAddLink && (
    <button
      type="button"
      onClick={onAddLink}
      data-onboarding="inventory-leakage-add-link"
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[#0E8965] dark:border-darkGreenColor-dark text-xs font-semibold text-[#0E8965] dark:text-darkGreenColor-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors"
    >
      <Link2 className="w-4 h-4" />
      {t('inventory.leakageDrawer.addLink')}
    </button>
  );

  return (
    <SlideOverShell onClose={onClose} overlayOpen={overlayOpen} onboardingId="inventory-leakage-drawer">
      {periodLabel && (
        <p className="text-sm font-medium text-[#0E8965] dark:text-darkGreenColor-dark mb-3">{periodLabel}</p>
      )}

      <h2 className="text-2xl sm:text-[28px] leading-8 sm:leading-[39px] font-bold tracking-[-0.1px] text-textPrimary dark:text-textPrimary-dark">
        {row.productName}
      </h2>

      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px] font-medium text-muted dark:text-muted-dark">
        {categoryLabel && (
          <>
            <span
              className="text-xs font-medium rounded-md px-2 py-1 whitespace-nowrap"
              style={getCategoryStyles(categoryLabel, darkMode)}
            >
              {categoryLabel}
            </span>
            <span aria-hidden>•</span>
          </>
        )}
        {supplier && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <SupplierLogo
                platform={Platform.fromString(supplier.platform)}
                unknownSupplierName={supplier.name}
                size="small"
                alt={supplier.name}
                onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
              />
              {supplier.name}
            </span>
            {row.groupName && <span aria-hidden>•</span>}
          </>
        )}
        {row.groupName && <span>{row.groupName}</span>}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Tint tint={CONFIDENCE_TINT[row.confidence]} label={t(`inventory.leakage.confidence.${row.confidence}`)} />
        <Tint tint={STATUS_TINT[row.status]} label={t(`inventory.leakage.status.${row.status}`)} />
      </div>

      {/* Reconciliation as cards — same order as the tab's strip. */}
      <SectionTitle className="mt-6">{t('inventory.leakage.detail.movementTitle')}</SectionTitle>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-2.5" data-onboarding="inventory-leakage-calc">
        <OverviewCard label={t('inventory.leakage.colOpening')} value={poolQty(row.startQty)} sub={money(row.startValue)} />
        <OverviewCard label={t('inventory.leakage.colPurchased')} value={poolQty(row.purchasedQty)} sub={money(row.purchasedValue)} />
        <OverviewCard label={t('inventory.leakage.colUsage')} value={poolQty(row.neededQty)} sub={money(row.neededValue)} />
        <OverviewCard label={t('inventory.leakage.tileExpected')} value={poolQty(row.expectedQty)} sub={money(row.expectedValue)} />
        <OverviewCard label={t('inventory.leakage.tileCounted')} value={poolQty(row.endQty)} sub={money(row.countedValue)} />
        <OverviewCard
          label={t('inventory.leakage.tileLeakage')}
          value={poolQty(row.varianceQty)}
          sub={money(row.varianceValue)}
          tone={leakageTone}
        />
      </div>
      {/* Why the cards above read "—". Two different causes wear the same dash, and telling
          the user "the alternatives measure differently" about a product whose OWN two counts
          drifted would send them to fix the wrong thing. */}
      {row.countUnitMismatch ? (
        <p className={cn('mt-2 text-sm', tertiaryText)}>
          {t(row.countQtyUnreadable ? 'inventory.leakage.countUnitUnreadable' : 'inventory.leakage.countUnitConverted', {
            unit: row.stdUnit,
          })}
        </p>
      ) : (
        !qtyComparable && <p className={cn('mt-2 text-sm', tertiaryText)}>{t('inventory.leakage.pool.mixedUnits')}</p>
      )}
      {row.varianceValue === null && (
        <p className={cn('mt-2 text-sm', tertiaryText)}>{t('inventory.leakage.noPriceTooltip')}</p>
      )}

      {/* Alternative pool members — the products this one row reconciles together. Facts only:
          a member's share of expected/leakage is an allocation estimate, so it stays on the row. */}
      {pool && (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <SectionTitle>{t('inventory.leakage.pool.membersTitle')}</SectionTitle>
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
              <ArrowLeftRight className="h-3 w-3" />
              {t('inventory.leakage.pool.badge', { count: pool.memberCount - 1 })}
            </span>
          </div>
          <p className={cn('mt-1 text-sm font-medium', mutedText)}>{t('inventory.leakage.pool.membersHint')}</p>

          <div className="mt-4 rounded-2xl border border-border dark:border-border-dark bg-backgroundDarker dark:bg-backgroundDarker-dark p-3 flex flex-col gap-3">
            {members.map((m) => (
              <div key={m.key} className="rounded-xl bg-background dark:bg-background-dark p-3 sm:p-4 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className={cn('truncate text-sm sm:text-base font-semibold', m.counted ? 'text-textPrimary dark:text-textPrimary-dark' : tertiaryText)}>
                      {m.productName}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span
                        className={cn(
                          'inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          m.isPrimary
                            ? 'bg-backgroundDarker dark:bg-backgroundDarker-dark text-textPrimary dark:text-textPrimary-dark'
                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                        )}
                      >
                        {t(m.isPrimary ? 'inventory.leakage.pool.primary' : 'inventory.leakage.pool.alternative')}
                      </span>
                      {!m.counted && (
                        <span
                          className={grayBadgeClass}
                          title={t(m.inert ? 'inventory.leakage.pool.inertHint' : 'inventory.leakage.pool.notCountedHint')}
                        >
                          {t(m.inert ? 'inventory.leakage.pool.inert' : 'inventory.leakage.pool.notCounted')}
                        </span>
                      )}
                      {/* '' when the recipe row carried no platform — no logo beats a wrong one. */}
                      {m.platform && (
                        <SupplierLogo
                          platform={Platform.fromString(m.platform)}
                          unknownSupplierName={m.unknownSupplierName}
                          size="small"
                          alt={m.unknownSupplierName ?? m.platform}
                        />
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className={cn('text-sm font-medium', mutedText)}>
                      {memberQty(m, m.startQty)} → {memberQty(m, m.endQty)}
                    </span>
                    {m.countedValue !== null && (
                      <p className={cn('text-xs font-medium', mutedText)}>{currencyFormatter.format(m.countedValue)}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className={cn('text-xs font-medium', mutedText)}>
                    {t('inventory.leakage.pool.colPurchased')}: {memberQty(m, m.purchasedQty)}
                  </span>
                  <span className={cn('text-xs font-medium', mutedText)} title={t('inventory.leakage.pool.estimateHint')}>
                    {t('inventory.leakage.pool.colUsage')}: ≈ {memberQty(m, m.allocatedNeededQty)}
                  </span>
                </div>
              </div>
            ))}

            {(pool.membersInOtherGroups ?? []).map((name) => (
              // Name-only by design: this row's sums exclude them entirely, so showing
              // quantities here would imply they were folded in. The engine sends names.
              <div key={`elsewhere-${name}`} className="rounded-xl bg-background dark:bg-background-dark p-3 sm:p-4 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <p className={cn('truncate text-sm sm:text-base font-semibold', tertiaryText)}>{name}</p>
                  <span className={cn(grayBadgeClass, 'shrink-0')} title={t('inventory.leakage.pool.otherGroupsMemberHint')}>
                    {t('inventory.leakage.pool.otherGroupsMember')}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {pool.membersUncounted.length > 0 && (
            <p className={cn('mt-2 text-sm', tertiaryText)}>
              {t('inventory.leakage.pool.uncountedHint', { names: pool.membersUncounted.join(', ') })}
            </p>
          )}
          {pool.membersExcluded.length > 0 && (
            <p className={cn('mt-1 text-sm', tertiaryText)}>
              {t('inventory.leakage.pool.excludedHint', { names: pool.membersExcluded.join(', ') })}
            </p>
          )}
          {pool.membersOpeningStockBlocked.length > 0 && (
            <p className={cn('mt-1 text-sm', tertiaryText)}>
              {t('inventory.leakage.pool.openingBlockedHint', { names: pool.membersOpeningStockBlocked.join(', ') })}
            </p>
          )}
        </>
      )}

      {/* Per-section changes */}
      {sectionChanges.length > 0 && (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <SectionTitle>{t('inventory.leakage.detail.sectionsTitle')}</SectionTitle>
            {row.moved && (
              <span className={badgeBaseClass} style={{ backgroundColor: iconBgAmber, color: '#B54708' }}>
                {t('inventory.leakage.flag.moved')}
              </span>
            )}
            {row.mixedSections && (
              <span className={badgeBaseClass} style={{ backgroundColor: iconBgAmber, color: '#B54708' }}>
                {t('inventory.leakage.mixed')}
              </span>
            )}
          </div>
          <div className="mt-4 rounded-2xl border border-border dark:border-border-dark bg-backgroundDarker dark:bg-backgroundDarker-dark p-3 flex flex-col gap-3" data-onboarding="inventory-leakage-section">
            {sectionChanges.map((s) => (
              <div key={s.sectionKey} className="rounded-xl bg-background dark:bg-background-dark p-3 sm:p-4 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-semibold text-textPrimary dark:text-textPrimary-dark truncate">
                      {s.sectionName}
                      {s.deleted && <span className={cn('ml-1 font-medium', tertiaryText)}>{t('inventory.leakage.deletedSection')}</span>}
                    </p>
                    <p className="mt-0.5 text-[12px] font-medium text-muted dark:text-muted-dark">
                      {poolQty(s.startQty)} → {poolQty(s.endQty)}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className={cn('text-base font-bold', deltaTone(s.deltaQty))}>{poolQty(s.deltaQty)}</span>
                    {s.deltaValue !== null && (
                      <p className="text-xs font-medium text-muted dark:text-muted-dark">{currencyFormatter.format(s.deltaValue)}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between gap-3 pt-3 border-t border-dashed border-border dark:border-border-dark">
              <span className="text-sm sm:text-base font-medium text-muted dark:text-muted-dark">
                {t('inventory.leakage.colChange')}
              </span>
              <span className="text-right">
                <span className={cn('text-lg sm:text-xl font-semibold', deltaTone(sectionTotals.qty))}>
                  {poolQty(sectionTotals.qty)}
                </span>
                {sectionTotals.value !== null && (
                  <span className="ml-2 text-sm font-medium text-muted dark:text-muted-dark">
                    {currencyFormatter.format(sectionTotals.value)}
                  </span>
                )}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Linked inventory product */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>{t('inventory.leakageDrawer.linkedTitle')}</SectionTitle>
        {onEditInventoryLink && (
          <button
            type="button"
            onClick={onEditInventoryLink}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-border dark:border-border-dark text-xs font-semibold text-textSecondary dark:text-textSecondary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            {t('inventory.leakageDrawer.editLink')}
          </button>
        )}
      </div>
      {linkedProduct ? (
        <div className="mt-3 rounded-xl border border-border dark:border-border-dark bg-background dark:bg-background-dark p-3 sm:p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <SupplierLogo
                platform={Platform.fromString(linkedProduct.platform)}
                unknownSupplierName={linkedProduct.unknownSupplierName}
                size="small"
                alt={linkedProduct.unknownSupplierName ?? linkedProduct.platform}
              />
              <div className="min-w-0">
                <p className="truncate text-sm sm:text-base font-semibold text-textPrimary dark:text-textPrimary-dark">
                  {linkedProduct.productName}
                </p>
                {linkedProduct.articleId && (
                  <p className={cn('text-xs font-medium', mutedText)}>
                    {t('inventory.leakageDrawer.articleId', { id: linkedProduct.articleId })}
                  </p>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right">
              {linkedProduct.lastOrderDate && (
                <p className={cn('text-xs font-medium', mutedText)}>
                  {t('inventory.leakageDrawer.lastOrdered', { date: getTableFormattedDate(linkedProduct.lastOrderDate) })}
                </p>
              )}
              {valuationPrice && (
                <p
                  className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark"
                  title={t('inventory.leakageDrawer.valuationPriceHint')}
                >
                  {currencyFormatter.format(valuationPrice.value)} / {valuationPrice.unit}
                </p>
              )}
              {valuationPrice && (
                <p className={cn('text-[11px] font-medium', mutedText)}>{t('inventory.leakageDrawer.valuationPrice')}</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p className={cn('mt-1 text-sm font-medium', mutedText)}>{t('inventory.leakageDrawer.linkedNoProduct')}</p>
      )}
      <p className={cn('mt-2 text-sm font-medium', mutedText)}>
        {sectionNames.length > 0
          ? t('inventory.leakageDrawer.linkedSections', { sections: sectionNames.join(', ') })
          : t('inventory.leakageDrawer.linkedNone')}
      </p>

      {/* Needed quantity breakdown — the same section the product-utilization drawer shows,
          answering "what was used" AND "where is this set up": recipes that sold nothing, and
          recipes that list this product only as a fallback, appear as zero-sold cards. */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-2" data-onboarding="inventory-leakage-usage">
        <SectionTitle>{t('reports.utilization.drawer.neededBreakdown')}</SectionTitle>
        {hasUsage && addLinkButton}
      </div>
      <p className="mt-1 text-sm font-medium text-muted dark:text-muted-dark">
        {t('reports.utilization.drawer.neededBreakdownHint', { qty: stdQty(row.neededQty) })}
      </p>
      {row.neededUnitMismatch && (
        <p className={cn('mt-2 text-sm', tertiaryText)}>
          {t(usageUnreadable ? 'inventory.leakage.recipeUnitDropped' : 'inventory.leakage.recipeUnitConverted', {
            unit: row.stdUnit,
          })}
        </p>
      )}

      {!hasUsage ? (
        <div className="mt-4 rounded-2xl border border-border dark:border-border-dark bg-backgroundDarker dark:bg-backgroundDarker-dark p-4 flex flex-col items-start gap-3">
          <p className="text-sm text-muted dark:text-muted-dark">
            {/* An empty breakdown means one of three different things, and only the first is
                "nobody linked a recipe". Name the real one, or the user re-links a product
                that is already linked. */}
            {t(
              row.countQtyUnreadable
                ? 'inventory.leakage.countUnitUnreadable'
                : qtyComparable
                  ? 'inventory.leakage.detail.noRecipeLink'
                  : 'inventory.leakage.pool.mixedUnits',
              { unit: row.stdUnit }
            )}
          </p>
          {addLinkButton}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-border dark:border-border-dark bg-backgroundDarker dark:bg-backgroundDarker-dark p-3 flex flex-col gap-5">
          {usageRows.map((u, i) => {
            // The prep breakdown, when this row's figures are exact enough to carry one
            // (`buildPerDishUsage` clears the routes on pool estimates and unit conversions).
            const prepLines = usageUnreadable
              ? []
              : prepPathLines(u.prepPaths, (v) => LEAKAGE_QTY_STD.rowPrecise(row, v), t);
            return (
            <BreakdownCard
              key={`${u.label}-${i}`}
              title={u.label}
              notes={prepLines}
              titleBadge={
                u.viaAlternative ? (
                  <span title={t('reports.utilization.drawer.viaAlternative')} className="shrink-0">
                    <ArrowLeftRight className="w-4 h-4 text-blue-600 dark:text-blue-300" aria-hidden />
                  </span>
                ) : u.viaSubrecipe && prepLines.length === 0 ? (
                  // Fallback only. Where the prep lines render they name the same prep AND
                  // carry its share, so the badge would just say it a second time — but on a
                  // pooled/converted row (no lines) it is the only prep attribution left.
                  <span className={grayBadgeClass}>{t('inventory.leakageDrawer.viaSubrecipe', { name: u.viaSubrecipe })}</span>
                ) : undefined
              }
              onEdit={onEditDish && u.costPriceId ? () => onEditDish(u.costPriceId as string) : undefined}
              onboardingId={u.label === leakageSetupDish() ? 'inventory-leakage-edit-dish' : undefined}
              perUnit={usageUnreadable ? '—' : LEAKAGE_QTY_STD.rowPrecise(row, u.basePerUnit)}
              perUnitSub={t('inventory.leakageDrawer.perDish')}
              units={String(u.unitsSold)}
              unitsSub={t('inventory.leakageDrawer.sold')}
              total={usageUnreadable ? '—' : stdQty(u.totalBase)}
            />
            );
          })}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3 pt-3 border-t border-dashed border-border dark:border-border-dark">
            <div className="text-sm sm:text-base font-medium text-muted dark:text-muted-dark">
              {t('inventory.leakageDrawer.totalSold')}:{' '}
              <span className="text-lg sm:text-xl font-semibold text-textPrimary dark:text-textPrimary-dark">{totalSold}</span>
            </div>
            <div className="sm:text-right text-sm sm:text-base font-medium text-muted dark:text-muted-dark">
              {t('inventory.leakage.detail.usageTotal')}:{' '}
              <span className="text-lg sm:text-xl font-semibold text-textPrimary dark:text-textPrimary-dark">
                {stdQty(row.neededQty)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Data-quality flags */}
      {flags.length > 0 && (
        <>
          <SectionTitle className="mt-6">{t('inventory.leakage.colFlags')}</SectionTitle>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {flags.map((f) => (
              <span key={f.key} title={f.label} className={grayBadgeClass}>
                {f.label}
              </span>
            ))}
          </div>
        </>
      )}

      {/* Possible causes — a static checklist, collapsed until asked for. */}
      <button
        type="button"
        onClick={() => setCausesOpen((o) => !o)}
        className="mt-6 flex items-center gap-1.5 text-base leading-[30px] font-semibold text-textPrimary dark:text-textPrimary-dark hover:opacity-80 transition-opacity"
        aria-expanded={causesOpen}
      >
        {causesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {t('inventory.leakage.detail.causesTitle')}
      </button>
      {causesOpen && (
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {CAUSE_KEYS.map((c) => (
            <li key={c} className="text-sm text-textSecondary dark:text-textSecondary-dark">
              {t(`inventory.leakage.detail.${c}`)}
            </li>
          ))}
        </ul>
      )}
    </SlideOverShell>
  );
};

export default LeakageProductDrawer;
